"use client";

import { useState } from "react";
import { ChevronDown, ChevronUp, MessageSquare, CheckCircle2, Paperclip } from "lucide-react";
import type { Rfi } from "@/lib/conduit";
import { StatusPill } from "@/components/ui";
import { fmtDate, fmtDateTime, RFI_ACTIONABLE_STATUSES } from "@/lib/format";
import RfiResponder from "./rfi-ui";

export default function RfiCard({
  rfi,
  customerId,
  actingEmail,
}: {
  rfi: Rfi;
  customerId?: string;
  actingEmail?: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const isActionable = RFI_ACTIONABLE_STATUSES.includes((rfi.status ?? "").toLowerCase());
  const rounds = rfi.rounds ?? [];
  const responsesByRound = (roundId: string) => (rfi.responses ?? []).filter((r) => r.roundId === roundId);

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-4 p-6 text-left"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="min-w-0">
          <h2 className="display truncate text-[20px]">{rfi.title ?? rfi.id}</h2>
          {!expanded && rfi.summary && (
            <p className="mt-1 truncate text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
              {rfi.summary}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {rfi.dueAt && (
            <span className="text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
              due {fmtDate(rfi.dueAt)}
            </span>
          )}
          <StatusPill status={rfi.status} />
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {expanded && (
        <div className="border-t px-6 pb-6 pt-4" style={{ borderColor: "var(--c-hairline)" }}>
          {rfi.summary && <p className="mb-4 text-[14px] font-light">{rfi.summary}</p>}

          {rounds.length === 0 ? (
            <p className="text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
              No round details available yet.
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {rounds.map((round) => {
                const responses = responsesByRound(round.id);
                return (
                  <div key={round.id} className="flex flex-col gap-2">
                    {rounds.length > 1 && (
                      <div className="eyebrow" style={{ color: "var(--c-ink-soft)" }}>
                        round {round.roundNumber}
                      </div>
                    )}

                    {round.ask && (
                      <div className="card-cream flex gap-3 p-4">
                        <MessageSquare size={16} className="mt-[2px] shrink-0" />
                        <div className="min-w-0">
                          <div className="mb-1 text-[11px] font-medium" style={{ color: "var(--c-ink-soft)" }}>
                            Compliance asked
                            {round.openedAt ? ` · ${fmtDateTime(round.openedAt)}` : ""}
                          </div>
                          <p className="text-[14px] font-light">{round.ask}</p>
                        </div>
                      </div>
                    )}

                    {responses.map((resp) => (
                      <div
                        key={resp.id}
                        className="ml-6 flex gap-3 rounded-[16px] p-4"
                        style={{ boxShadow: "inset 0 0 0 1px var(--c-hairline)" }}
                      >
                        <CheckCircle2 size={16} className="mt-[2px] shrink-0" style={{ color: "var(--c-green)" }} />
                        <div className="min-w-0">
                          <div className="mb-1 text-[11px] font-medium" style={{ color: "var(--c-ink-soft)" }}>
                            {resp.submittedByEmail ? `${resp.submittedByEmail} responded` : "Response sent"}
                            {resp.createdAt ? ` · ${fmtDateTime(resp.createdAt)}` : ""}
                          </div>
                          <p className="text-[14px] font-light">{resp.message}</p>
                          {!!resp.documentIds?.length && (
                            <div
                              className="mt-2 flex items-center gap-1 text-[12px] font-light"
                              style={{ color: "var(--c-ink-soft)" }}
                            >
                              <Paperclip size={12} />
                              {resp.documentIds.length} document{resp.documentIds.length === 1 ? "" : "s"} attached
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {round.status === "responded" && round.closedAt && (
                      <p className="ml-6 text-[11px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                        round closed {fmtDateTime(round.closedAt)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 flex items-center gap-4">
            <span className="mono text-[11px]" style={{ color: "var(--c-ink-soft)" }}>
              {rfi.id}
            </span>
            {rfi.publishedAt && (
              <span className="text-[11px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                published {fmtDateTime(rfi.publishedAt)}
              </span>
            )}
          </div>

          {isActionable && customerId && (
            <div className="mt-4">
              <RfiResponder rfiId={rfi.id} customerId={customerId} actingEmail={actingEmail} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
